import { formatLongDate, type SenderInfo } from "@gsignatures/core";
import type { Who } from "../strings";

/** Names the strings catalog needs, from what the server sends. */
export function who(s: SenderInfo): Who {
  return {
    sender: s.name,
    legal: s.legalName,
    contact: s.contactName,
    contactFirst: s.contactName.trim().split(/\s+/)[0] ?? s.contactName,
    contactEmail: s.contactEmail,
  };
}

/** "October 24, 2026" in the signer's own time zone. */
export function longDate(isoTime: string): string {
  const d = new Date(isoTime);
  if (Number.isNaN(d.getTime())) return isoTime;
  return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(d);
}

/** "10:32 AM" in the signer's time zone. */
export function shortTime(isoTime: string): string {
  const d = new Date(isoTime);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(d);
}

/** "on October 24, 2026 at 10:33 AM GMT+5:30" — date, time and the zone's name, as the design asks. */
export function whenWithZone(isoTime: string): string {
  const d = new Date(isoTime);
  if (Number.isNaN(d.getTime())) return isoTime;
  const date = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(d);
  const time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(d);
  return `on ${date} at ${time}`;
}

/** "10-24-2026 10:32 GMT+5:30" — the stamp drawn beside a signature. */
export function stamp(isoTime: string): string {
  const d = new Date(isoTime);
  if (Number.isNaN(d.getTime())) return isoTime;
  return `${mdy(d)} ${new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZoneName: "short" }).format(d)}`;
}

/** MM-DD-YYYY in the signer's time zone (the date printed on the IRS form). */
export function mdy(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()}`;
}

/** "2029-12-31" → "December 31, 2029". */
export const isoDayLong = formatLongDate;

/** "7f3a 91c2 … e04b" — enough of a SHA-256 to compare by eye. */
export function shortHash(hex: string): string {
  const h = hex.replace(/[^0-9a-f]/gi, "").toLowerCase();
  if (h.length < 16) return h;
  return `${h.slice(0, 4)} ${h.slice(4, 8)} … ${h.slice(-4)}`;
}

/** "Golden Sparrow" → "GS". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
  return (first + second).toUpperCase();
}

/** Dark or light text on a brand colour, whichever reads better. */
export function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#ffffff";
  const n = parseInt(m[1]!, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
  return lum > 0.4 ? "#2a1800" : "#ffffff";
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** The mailto: for writing to the sender's contact. */
export function mailto(email: string, subject?: string): string {
  return `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;
}
